import { Component, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Volume2 } from "lucide-react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { LangProvider, useLang } from "./i18n";
import { QuoteProvider } from "./quote";
import { ENTRY_AUDIO } from "./constants";
import Preloader from "./components/Preloader";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Story from "./components/Story";
import Founder from "./components/Founder";
import Products from "./components/Products";
import Markets from "./components/Markets";
import Franchise from "./components/Franchise";
import Profumi from "./components/Profumi";
import Footer from "./components/Footer";
import QuoteDrawer from "./components/QuoteDrawer";
import ChatWidget from "./components/ChatWidget";
import AdminPage from "./components/AdminPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 text-center">
          <p className="font-display text-sm tracking-[0.3em] text-gold">III ROMA — RENOVATA AVRA · PLEASE RELOAD</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute("href"));
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: 0, duration: 1.6 });
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);
  return null;
}

let entryAudio = null;
let pendingAudio = false;

function Home() {
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [audioOn, setAudioOn] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);

  const startAudio = () => {
    if (!entryAudio) {
      entryAudio = new Audio(ENTRY_AUDIO);
      entryAudio.volume = 0.6;
      entryAudio.setAttribute("data-testid", "entry-audio");
      document.body.appendChild(entryAudio);
    }
    entryAudio
      .play()
      .then(() => {
        setAudioOn(true);
        setAudioBlocked(false);
      })
      .catch(() => {
        // Browser autoplay policy: unmuted play() is rejected until first interaction.
        setAudioBlocked(true);
        if (pendingAudio) return;
        pendingAudio = true;
        const kick = () => {
          entryAudio
            .play()
            .then(() => {
              setAudioOn(true);
              setAudioBlocked(false);
            })
            .catch(() => {});
          document.removeEventListener("pointerdown", kick);
          document.removeEventListener("keydown", kick);
          document.removeEventListener("touchstart", kick);
        };
        document.addEventListener("pointerdown", kick);
        document.addEventListener("keydown", kick);
        document.addEventListener("touchstart", kick);
      });
  };

  const toggleAudio = () => {
    if (!entryAudio) return;
    if (entryAudio.paused) {
      entryAudio
        .play()
        .then(() => {
          setAudioOn(true);
          setAudioBlocked(false);
        })
        .catch(() => {});
    } else {
      entryAudio.pause();
      setAudioOn(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "";
  }, [loading]);

  useEffect(() => {
    startAudio();
  }, []);

  return (
    <QuoteProvider>
      <div className="grain bg-obsidian min-h-screen" data-testid="site-root">
        <AnimatePresence>{loading && <Preloader key="preloader" />}</AnimatePresence>
        <Nav />
        <main>
          <Hero start={!loading} />
          <Marquee />
          <Story />
          <Products />
          <Markets />
          <Franchise />
          <Profumi />
          <Founder />
        </main>
        <Footer />
        <QuoteDrawer />
        <ChatWidget />
        {audioBlocked && !audioOn && (
          <button
            data-testid="audio-enable-button"
            onClick={toggleAudio}
            className="fixed bottom-6 start-6 z-[80] flex items-center gap-2.5 border border-gold/40 bg-obsidian/85 px-4 py-2.5 text-[0.62rem] uppercase tracking-[0.28em] text-gold backdrop-blur-md transition-colors hover:bg-gold/15"
          >
            <Volume2 size={13} />
            {t("audio.enable")}
          </button>
        )}
        <Toaster
          position="bottom-center"
          toastOptions={{
            style: { background: "#140F0F", border: "1px solid rgba(212,175,55,0.3)", color: "#FDF5E6" },
          }}
        />
      </div>
    </QuoteProvider>
  );
}

function App() {
  return (
    <LangProvider>
      <SmoothScroll />
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </LangProvider>
  );
}

export default App;
